import { LoaderIcon } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactZoomPanPinchHandlers } from 'react-zoom-pan-pinch';

import { BroadcastingActionsBar } from './components/actions-bar/BroadcastingActionsBar';
import { BroadcastPane } from './components/broadcast-pane/BroadcastPane';
import { CommentsPane } from './components/comments-pane/CommentsPane';
import { ResizablePanes } from './components/resizable-panes/ResizablePanes';
import styles from './ImageBroadcastingWidget.module.scss';
import type { ImageBroadcastingWidgetState } from './model/ImageBroadcastingWidgetState';

type ImageBroadcastingWidgetProps = {
  widgetState: ImageBroadcastingWidgetState;
};

export const ImageBroadcastingWidget = observer(function ImageBroadcastingContent({
  widgetState,
}: ImageBroadcastingWidgetProps) {
  const { t } = useTranslation('imageBroadcast');
  const reactZoomPanPinchContent = useRef<ReactZoomPanPinchHandlers>(null);

  const { broadcasts: isBroadcastVisible, comments: isCommentsVisible } = widgetState.visiblePanels;

  const disableHover = Boolean(widgetState.appParams.resizing || widgetState.appParams.moving);

  const loading =
    widgetState.status !== 'ready' ||
    widgetState.wellbores.inProgress ||
    widgetState.imageBroadcasts.inProgress ||
    (widgetState.selectedBroadcast && !widgetState.selectedBroadcast.image && widgetState.selectedBroadcast.inProgress);

  return (
    <div className={styles.WidgetWrapper}>
      {loading && (
        <div className={styles.LoaderWrapper}>
          <LoaderIcon />
        </div>
      )}
      {!loading &&
        (widgetState.imageBroadcasts.list.length === 0 || !widgetState.currentImageUrl ? (
          <div className={styles.NoBroadcast}>{t('negativeCase.broadcastsEmpty')}</div>
        ) : (
          <ResizablePanes
            left={<BroadcastPane widgetState={widgetState} ref={reactZoomPanPinchContent} />}
            right={<CommentsPane widgetState={widgetState} />}
            leftVisible={isBroadcastVisible}
            rightVisible={isCommentsVisible}
            rightRatio={widgetState.commentsPanelRatio}
            onRightRatioChange={widgetState.setCommentsPanelRatio}
          />
        ))}
      <div className={styles.ActionsBarWrapper} style={{ pointerEvents: disableHover ? 'none' : 'auto' }}>
        <BroadcastingActionsBar
          widgetState={widgetState}
          resetScale={() => {
            reactZoomPanPinchContent?.current?.resetTransform();
          }}
        />
      </div>
    </div>
  );
});
